import type { NotificationEmailSettingsMap, NotificationType } from 'types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';

type EmailSettingsResponse = {
  settings: NotificationEmailSettingsMap;
};

const QUERY_KEY = ['NOTIFICATION_EMAIL_SETTINGS'];

/**
 * The signed-in user's own email preferences.
 *
 * Every type arrives with the server's defaults already merged in, so this hook
 * never re-implements the fallback rule - a default changed on the server moves
 * the switch here without a release.
 */
export const useEmailSettings = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const query = useQuery<EmailSettingsResponse>({
    queryKey: QUERY_KEY,
    queryFn: () => client.get<EmailSettingsResponse>('/notifications/settings/email'),
  });

  const update = useMutation({
    mutationFn: (settings: Partial<Record<NotificationType, boolean>>) =>
      client.put<EmailSettingsResponse>('/notifications/settings/email', { settings }),
    // The response is the full merged map, so it replaces the cache outright
    // rather than triggering a refetch of what we were just told.
    onSuccess: (data) => queryClient.setQueryData(QUERY_KEY, data),
  });

  const toggle = (type: NotificationType, enabled: boolean) => update.mutate({ [type]: enabled });

  return {
    settings: query.data?.settings,
    isLoading: query.isLoading,
    isSaving: update.isPending,
    error: update.error,
    toggle,
  };
};
